import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, Check, X, Database, Lightbulb } from 'lucide-react';
import { toast, confirm as confirmModal } from '../ui/Toast';

const CommKnowledgeAdmin = () => {
  // Tab management
  const [activeTab, setActiveTab] = useState('kb');

  // Knowledge Base state
  const [entries, setEntries] = useState([]);
  const [contextFilter, setContextFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showKbModal, setShowKbModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [kbLoading, setKbLoading] = useState(false);

  // Suggestions state
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsCount, setSuggestionsCount] = useState(0);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);

  // Modal form state
  const [formData, setFormData] = useState({
    context: 'itriia',
    category: '',
    title: '',
    content: '',
    target_level: 'initie',
    target_domain: 'all',
    tags: ''
  });

  const token = localStorage.getItem('oracle_token');
  const axiosConfig = {
    headers: { Authorization: `Bearer ${token}` }
  };

  // Fetch knowledge base entries
  useEffect(() => {
    if (activeTab === 'kb') {
      fetchKnowledgeBase();
    }
  }, [activeTab, contextFilter, categoryFilter]);

  // Fetch suggestions and count
  useEffect(() => {
    if (activeTab === 'suggestions') {
      fetchSuggestions();
      fetchSuggestionsCount();
    }
  }, [activeTab]);

  const fetchKnowledgeBase = async () => {
    try {
      setKbLoading(true);
      const response = await axios.get('/api/comm/knowledge', {
        params: {
          context: contextFilter,
          category: categoryFilter
        },
        ...axiosConfig
      });
      setEntries(response.data.entries || []);
    } catch (error) {
      console.error('Erreur lors du chargement de la base de connaissances:', error);
    } finally {
      setKbLoading(false);
    }
  };

  const fetchSuggestions = async () => {
    try {
      setSuggestionsLoading(true);
      const response = await axios.get('/api/comm/suggestions', {
        params: { status: 'pending' },
        ...axiosConfig
      });
      setSuggestions(response.data.suggestions || []);
    } catch (error) {
      console.error('Erreur lors du chargement des suggestions:', error);
    } finally {
      setSuggestionsLoading(false);
    }
  };

  const fetchSuggestionsCount = async () => {
    try {
      const response = await axios.get('/api/comm/suggestions/count', axiosConfig);
      setSuggestionsCount(response.data.count || 0);
    } catch (error) {
      console.error('Erreur lors du chargement du nombre de suggestions:', error);
    }
  };

  const openKbModal = (entry = null) => {
    if (entry) {
      setEditingEntry(entry);
      setFormData({
        context: entry.context || 'itriia',
        category: entry.category || '',
        title: entry.title || '',
        content: entry.content || '',
        target_level: entry.target_level || 'initie',
        target_domain: entry.target_domain || 'all',
        tags: entry.tags ? entry.tags.join(', ') : ''
      });
    } else {
      setEditingEntry(null);
      setFormData({
        context: 'itriia',
        category: '',
        title: '',
        content: '',
        target_level: 'initie',
        target_domain: 'all',
        tags: ''
      });
    }
    setShowKbModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const saveKbEntry = async () => {
    if (!formData.category || !formData.title || !formData.content) {
      toast.warning('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      const payload = {
        context: formData.context,
        category: formData.category,
        title: formData.title,
        content: formData.content,
        target_level: formData.target_level,
        target_domain: formData.target_domain,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(t => t) : []
      };

      if (editingEntry) {
        await axios.put(`/api/comm/knowledge/${editingEntry.id}`, payload, axiosConfig);
      } else {
        await axios.post('/api/comm/knowledge', payload, axiosConfig);
      }

      setShowKbModal(false);
      fetchKnowledgeBase();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      toast.error('Erreur lors de la sauvegarde de l\'entree');
    }
  };

  const deleteKbEntry = async (id) => {
    const confirmed = await confirmModal('Êtes-vous sûr de vouloir supprimer cette entrée ?');
    if (!confirmed) {
      return;
    }

    try {
      await axios.delete(`/api/comm/knowledge/${id}`, axiosConfig);
      fetchKnowledgeBase();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      toast.error('Erreur lors de la suppression de l\'entrée');
    }
  };

  const approveSuggestion = async (suggestionId, suggestion) => {
    try {
      const payload = {
        modified_content: suggestion.content,
        modified_title: suggestion.title,
        modified_category: suggestion.category
      };
      await axios.put(`/api/comm/suggestions/${suggestionId}/approve`, payload, axiosConfig);
      fetchSuggestions();
      fetchSuggestionsCount();
    } catch (error) {
      console.error('Erreur lors de l\'approbation:', error);
      toast.error('Erreur lors de l\'approbation de la suggestion');
    }
  };

  const rejectSuggestion = async (suggestionId) => {
    const notes = await confirmModal('Notes de rejet (optionnel):', { isPrompt: true });

    try {
      const payload = {
        notes: notes || ''
      };
      await axios.put(`/api/comm/suggestions/${suggestionId}/reject`, payload, axiosConfig);
      fetchSuggestions();
      fetchSuggestionsCount();
    } catch (error) {
      console.error('Erreur lors du rejet:', error);
      toast.error('Erreur lors du rejet de la suggestion');
    }
  };

  const styles = {
    container: {
      padding: '24px',
      color: 'var(--text)',
      backgroundColor: 'var(--card-bg)',
      minHeight: '100vh'
    },
    header: {
      marginBottom: '24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    title: {
      fontSize: '24px',
      fontWeight: 'bold',
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    tabContainer: {
      display: 'flex',
      gap: '8px',
      marginBottom: '24px',
      borderBottom: `1px solid var(--card-border)`
    },
    tab: {
      padding: '12px 24px',
      border: 'none',
      backgroundColor: 'transparent',
      color: 'var(--text-secondary)',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      borderBottom: '2px solid transparent',
      transition: 'all 0.2s'
    },
    tabActive: {
      color: 'var(--accent)',
      borderBottomColor: 'var(--accent)'
    },
    filterSection: {
      display: 'flex',
      gap: '12px',
      marginBottom: '24px',
      flexWrap: 'wrap'
    },
    select: {
      padding: '8px 12px',
      backgroundColor: 'var(--input-bg)',
      border: `1px solid var(--input-border)`,
      color: 'var(--text)',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '14px'
    },
    button: {
      padding: '8px 16px',
      backgroundColor: 'var(--accent)',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      transition: 'opacity 0.2s'
    },
    buttonDanger: {
      backgroundColor: '#3b82f6',
      color: 'white'
    },
    buttonSuccess: {
      backgroundColor: '#22c55e',
      color: 'white'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      marginBottom: '24px'
    },
    th: {
      padding: '12px',
      textAlign: 'left',
      fontSize: '13px',
      fontWeight: '600',
      color: 'var(--text-secondary)',
      borderBottom: `1px solid var(--card-border)`,
      backgroundColor: 'var(--overlay-bg)'
    },
    td: {
      padding: '12px',
      borderBottom: `1px solid var(--card-border)`,
      fontSize: '14px'
    },
    tdActions: {
      display: 'flex',
      gap: '8px',
      flexWrap: 'wrap'
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'var(--overlay-bg)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    },
    modalContent: {
      backgroundColor: 'var(--modal-bg)',
      border: `1px solid var(--modal-border)`,
      borderRadius: '8px',
      padding: '24px',
      maxWidth: '600px',
      width: '90%',
      maxHeight: '90vh',
      overflowY: 'auto',
      boxShadow: 'var(--shadow-lg)'
    },
    formGroup: {
      marginBottom: '16px'
    },
    label: {
      display: 'block',
      marginBottom: '6px',
      fontSize: '13px',
      fontWeight: '500',
      color: 'var(--text-secondary)'
    },
    input: {
      width: '100%',
      padding: '10px 12px',
      backgroundColor: 'var(--input-bg)',
      border: `1px solid var(--input-border)`,
      color: 'var(--text)',
      borderRadius: '6px',
      fontSize: '14px',
      boxSizing: 'border-box'
    },
    textarea: {
      width: '100%',
      padding: '10px 12px',
      backgroundColor: 'var(--input-bg)',
      border: `1px solid var(--input-border)`,
      color: 'var(--text)',
      borderRadius: '6px',
      fontSize: '14px',
      boxSizing: 'border-box',
      minHeight: '120px',
      fontFamily: 'inherit',
      resize: 'vertical'
    },
    suggestionItem: {
      backgroundColor: 'var(--card-bg)',
      border: `1px solid var(--card-border)`,
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '12px',
      display: 'flex',
      flexDirection: 'column',
      gap: '12px'
    },
    suggestionTitle: {
      fontSize: '15px',
      fontWeight: '600',
      color: 'var(--text)'
    },
    suggestionMeta: {
      fontSize: '13px',
      color: 'var(--text-secondary)',
      display: 'flex',
      gap: '16px',
      flexWrap: 'wrap'
    },
    suggestionPreview: {
      fontSize: '13px',
      color: 'var(--text-muted)',
      lineHeight: '1.5',
      marginBottom: '8px'
    },
    suggestionActions: {
      display: 'flex',
      gap: '8px'
    },
    badge: {
      display: 'inline-block',
      padding: '2px 8px',
      backgroundColor: 'var(--accent-light)',
      color: 'var(--accent)',
      borderRadius: '4px',
      fontSize: '12px',
      fontWeight: '500'
    },
    emptyState: {
      textAlign: 'center',
      padding: '48px 24px',
      color: 'var(--text-muted)'
    },
    loadingState: {
      textAlign: 'center',
      padding: '48px 24px',
      color: 'var(--text-secondary)'
    }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.title}>
          <Database size={28} />
          Gestion Base de Connaissances Communication
        </div>
      </div>

      {/* Tabs */}
      <div style={styles.tabContainer}>
        <button
          style={{
            ...styles.tab,
            ...(activeTab === 'kb' ? styles.tabActive : {})
          }}
          onClick={() => setActiveTab('kb')}
        >
          Base de connaissances
        </button>
        <button
          style={{
            ...styles.tab,
            ...(activeTab === 'suggestions' ? styles.tabActive : {})
          }}
          onClick={() => setActiveTab('suggestions')}
        >
          Suggestions {suggestionsCount > 0 && <span style={styles.badge}>{suggestionsCount}</span>}
        </button>
      </div>

      {/* Tab 1: Knowledge Base */}
      {activeTab === 'kb' && (
        <div>
          {/* Filters */}
          <div style={styles.filterSection}>
            <select
              style={styles.select}
              value={contextFilter}
              onChange={(e) => setContextFilter(e.target.value)}
            >
              <option value="">Tous les contextes</option>
              <option value="itriia">ITRIIA</option>
              <option value="rh">RH</option>
            </select>

            <input
              type="text"
              style={styles.select}
              placeholder="Filtrer par catégorie..."
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            />

            <button
              style={styles.button}
              onClick={() => openKbModal()}
            >
              <Plus size={16} />
              Ajouter une entrée
            </button>
          </div>

          {/* Knowledge Base Table */}
          {kbLoading ? (
            <div style={styles.loadingState}>Chargement...</div>
          ) : entries.length === 0 ? (
            <div style={styles.emptyState}>Aucune entrée trouvée</div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Titre</th>
                  <th style={styles.th}>Catégorie</th>
                  <th style={styles.th}>Contexte</th>
                  <th style={styles.th}>Niveau</th>
                  <th style={styles.th}>Domaine</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {entries.map(entry => (
                  <tr key={entry.id}>
                    <td style={styles.td}>{entry.title}</td>
                    <td style={styles.td}>{entry.category}</td>
                    <td style={styles.td}>
                      <span style={styles.badge}>{entry.context}</span>
                    </td>
                    <td style={styles.td}>{entry.target_level}</td>
                    <td style={styles.td}>{entry.target_domain}</td>
                    <td style={styles.td}>
                      <div style={styles.tdActions}>
                        <button
                          style={styles.button}
                          onClick={() => openKbModal(entry)}
                          title="Modifier"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          style={{ ...styles.button, ...styles.buttonDanger }}
                          onClick={() => deleteKbEntry(entry.id)}
                          title="Supprimer"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* KB Modal */}
          {showKbModal && (
            <div style={styles.modal} onClick={() => setShowKbModal(false)}>
              <div
                style={styles.modalContent}
                onClick={(e) => e.stopPropagation()}
              >
                <h2 style={{ marginBottom: '20px', fontSize: '18px', fontWeight: 'bold' }}>
                  {editingEntry ? 'Modifier une entrée' : 'Ajouter une entrée'}
                </h2>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Contexte *</label>
                  <select
                    style={styles.input}
                    name="context"
                    value={formData.context}
                    onChange={handleFormChange}
                  >
                    <option value="itriia">ITRIIA</option>
                    <option value="rh">RH</option>
                  </select>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Catégorie *</label>
                  <input
                    type="text"
                    style={styles.input}
                    name="category"
                    value={formData.category}
                    onChange={handleFormChange}
                    placeholder="ex: Communication, Leadership"
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Titre *</label>
                  <input
                    type="text"
                    style={styles.input}
                    name="title"
                    value={formData.title}
                    onChange={handleFormChange}
                    placeholder="Titre de l'entrée"
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Contenu *</label>
                  <textarea
                    style={styles.textarea}
                    name="content"
                    value={formData.content}
                    onChange={handleFormChange}
                    placeholder="Contenu détaillé"
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Niveau cible *</label>
                  <select
                    style={styles.input}
                    name="target_level"
                    value={formData.target_level}
                    onChange={handleFormChange}
                  >
                    <option value="neophyte">Néophyte</option>
                    <option value="initie">Initié</option>
                    <option value="expert">Expert</option>
                  </select>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Domaine cible *</label>
                  <select
                    style={styles.input}
                    name="target_domain"
                    value={formData.target_domain}
                    onChange={handleFormChange}
                  >
                    <option value="all">Tous les domaines</option>
                    <option value="entreprise">Entreprise</option>
                    <option value="politique">Politique</option>
                    <option value="scientifique">Scientifique</option>
                    <option value="citoyen">Citoyen</option>
                  </select>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Tags (séparés par des virgules)</label>
                  <input
                    type="text"
                    style={styles.input}
                    name="tags"
                    value={formData.tags}
                    onChange={handleFormChange}
                    placeholder="tag1, tag2, tag3"
                  />
                </div>

                <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                  <button
                    style={styles.button}
                    onClick={saveKbEntry}
                  >
                    <Check size={16} />
                    Enregistrer
                  </button>
                  <button
                    style={{ ...styles.button, backgroundColor: 'var(--text-muted)', color: 'var(--text)' }}
                    onClick={() => setShowKbModal(false)}
                  >
                    Annuler
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tab 2: Suggestions */}
      {activeTab === 'suggestions' && (
        <div>
          {suggestionsLoading ? (
            <div style={styles.loadingState}>Chargement des suggestions...</div>
          ) : suggestions.length === 0 ? (
            <div style={styles.emptyState}>Aucune suggestion en attente</div>
          ) : (
            <div>
              {suggestions.map(suggestion => (
                <div key={suggestion.id} style={styles.suggestionItem}>
                  <div style={styles.suggestionTitle}>{suggestion.title}</div>

                  <div style={styles.suggestionMeta}>
                    <span><strong>Contexte:</strong> {suggestion.context}</span>
                    <span><strong>Catégorie:</strong> {suggestion.category}</span>
                    <span><strong>Source:</strong> {suggestion.source_type}</span>
                    <span><strong>Date:</strong> {new Date(suggestion.created_at).toLocaleDateString('fr-FR')}</span>
                  </div>

                  <div style={styles.suggestionPreview}>
                    {suggestion.content.substring(0, 200)}
                    {suggestion.content.length > 200 && '...'}
                  </div>

                  <div style={styles.suggestionActions}>
                    <button
                      style={{ ...styles.button, ...styles.buttonSuccess }}
                      onClick={() => approveSuggestion(suggestion.id, suggestion)}
                    >
                      <Check size={16} />
                      Approuver
                    </button>
                    <button
                      style={{ ...styles.button, ...styles.buttonDanger }}
                      onClick={() => rejectSuggestion(suggestion.id)}
                    >
                      <X size={16} />
                      Rejeter
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CommKnowledgeAdmin;
